import isEqual from 'lodash/isEqual';
import { memo, useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import ExtractionDataIcon from '~/common/componentMapper/QuestionExtraction/components/ExtractionDataIconComponent';
import Flex from '~/common/components/Flex';
import { getError } from '~/common/components/QuestionLibrary/utils/errorHandling';

import SmartAnswer from '../components/smartQuestionComponents/smartAnswer/SmartAnswer';
import ConditionEvaluator from '../components/utilityComponents/ConditionEvaluator';
import FocusedWrapper from '../components/utilityComponents/FocusedWrapper';
import QuestionResolutionWrapper from '../components/utilityComponents/QuestionResolutionWrapper';
import QuestionMapper from '../mappers/QuestionMapper';
import { useQuestionLibraryStore } from '../store/questionLibraryStore';
import type { QuestionProp } from '../types/question';
import buildRules from '../utils/buildRules';
import { useIsFocused, useSelectSmartAnswer } from '../utils/hooks';
import { useConditionsEvaluator } from '../utils/useConditionsEvaluator';

type QuestionControllerProps = {
  question?: QuestionProp;
  hasBottomBorder?: boolean;
  isParentShowing?: boolean;
  hasTopBorder?: boolean;
};

function QuestionController({
  question,
  hasBottomBorder = true,
  hasTopBorder = false,
  isParentShowing = true,
}: Readonly<QuestionControllerProps>) {
  const {
    control,
    formState: { errors },
    getValues,
    watch,
  } = useFormContext();

  const screenData = useQuestionLibraryStore((s) => s.screenData);

  const { isVisible, isDisabled } = useConditionsEvaluator(
    question.conditions ?? {},
    question.metadata,
  );
  const isAiHidden = screenData?.isAiHiddenByQuestion?.[question.name] === true;
  const isShowing = isVisible && isParentShowing && !isAiHidden;
  const { field } = useController({
    name: question.name,
    control,
    rules: isDisabled
      ? {}
      : buildRules(question, isShowing, {
          screenData,
          formData: watch(),
        }),
    defaultValue: getValues()[question.name],
  });

  useEffect(() => {
    // To remove error state from disabled fields
    if (isDisabled) control.trigger(question.name);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isDisabled, field]);

  const smartAnswer = useSelectSmartAnswer(
    question.smartAnswerGuid ?? question.name,
  );

  if (smartAnswer && Object.keys(smartAnswer).length > 0) {
    const { secondaryIconType, ...primarySmartAnswer } = smartAnswer;
    question.smartAnswer = primarySmartAnswer;
    if (secondaryIconType) {
      question.secondarySmartIcon = { iconType: secondaryIconType };
    }
  }

  return (
    <ConditionEvaluator
      conditionResult={isShowing}
      conditions={question.conditions}
      name={question.name}
    >
      <QuestionResolutionWrapper
        hasBottomBorder={question.hasBottomBorder ?? hasBottomBorder}
        hasTopBorder={question.hasTopBorder ?? hasTopBorder}
        resolutions={question.resolutions}
        padding={question.contentPadding}
        flex={question.flex}
      >
        <FocusedWrapper
          isFocused={useIsFocused(question.name) && question.enableFocus}
        >
          <Flex
            alignItems={'flex-start'}
            paddingTop={question.paddingTop ?? '1em'}
            position={'relative'}
          >
            <QuestionMapper
              type={question.type}
              question={question}
              field={field}
              error={getError(errors, field.name)}
              disabled={isDisabled}
            />
            {question?.secondarySmartIcon && (
              <ExtractionDataIcon
                testId={`${question.name}-extraction-icon-secondary-${question.secondarySmartIcon?.iconType}`}
                iconType={question.secondarySmartIcon?.iconType}
                sx={question.secondarySmartAnswerSx ?? {}}
              />
            )}
          </Flex>
          {question.smartAnswer?.aiResponse && (
            <SmartAnswer
              smartAnswer={question.smartAnswer}
              detailedAnswer={question.smartAnswer?.aiResponse.detailedAnswer}
              iconType={question.smartAnswer?.iconType}
            />
          )}
        </FocusedWrapper>
      </QuestionResolutionWrapper>
    </ConditionEvaluator>
  );
}

export default memo(QuestionController, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
